import event from '../../../utils/analytics/event';
export var addScript = function addScript(appId) {
  if (typeof window === 'undefined' || window.fbAsyncInit) {
    return;
  }

  window.fbAsyncInit = function () {
    FB.init({
      appId: appId,
      autoLogAppEvents: true,
      xfbml: true,
      version: 'v6.0'
    });
  };

  var script = document.createElement('script');
  script.src = '//connect.facebook.net/en_US/sdk.js';
  script.async = true;
  document.body.appendChild(script);
};
export var FBcallback = function FBcallback(response, page, piwikEvents) {
  if (!response) {
    event(piwikEvents ? piwikEvents.failure : ['share', 'facebook-failure', page]);
  } else {
    event(piwikEvents ? piwikEvents.success : ['share', 'facebook-success', page]);
  }
};
/**
 * Custom hook which loads the Facebook SDK automatically and provides facebook sharing functionality anywhere within the app.
 *
 * ```js
 * import { useFacebookShare } from '@minerva-analytics/pantheon';
 *
 * const Test = () => {
 *
 *   const facebookShare = useFacebookShare({
 *     url: 'https://url.com',
 *     description: 'example description',
 *     page: 'test-page',
 *     appId: '12345678'
 *   });
 *
 *   return <button onClick={() => facebookShare()}>Facebook Button</button>;
 * }
 * ```
 *
 * By default, it logs 3 possible piwik events:
 *
 * - `event(['share', 'facebook-start', page]);`
 *    - A user starts the share action
 * - `event(['share', 'facebook-success', page]);`
 *    - A user succesfully shared to facebook
 * - `event(['share', 'facebook-failure', page]);`
 *    - A user either cancelled their share or the share failed
 *
 * If an object is passed for the piwikEvents param, then 3 custom piwik events will be logged instead of the default options.
 *
 * `piwikEvents` must be passed in as such: `{start: [], success:[], failure:[]}`
 *
 * ```js
 * import { useFacebookShare } from '@minerva-analytics/pantheon';
 *
 * const Test = () => {
 *   const facebookShare = useFacebookShare({
 *     url: 'https://url.com',
 *     description: 'example description',
 *     appId: '12345678'
 *     piwikEvents: {start: ['click', 'start'], success: ['click', 'success'], failure: ['click', 'failure']}
 *   });
 *
 *   return <button onClick={() => facebookShare()}>Facebook Button</button>;
 * }
 * ```
 *
 * @param {object} options - Options for the hook
 * @param {string} options.url - URL that user is intending to share
 * @param {string} options.description - Custom description for the URL above
 * @param {string} options.page - The page that this hook will be used on. This is used for creating unique piwik events per page.
 * @param {string} options.appId - Facebook app ID
 * @param {object} options.piwikEvents - Optional object consiting of 3 custom events: start, success, failure. Object keys must be `start`, `success` and `failure` with values as arrays in the form of [category, action, name, value], though name and value are optional.
 *
 * @returns {function} - Function that allows user to share with Facebook
 */

var useFacebookShare = function useFacebookShare(_ref) {
  var url = _ref.url,
      description = _ref.description,
      page = _ref.page,
      appId = _ref.appId,
      _ref$piwikEvents = _ref.piwikEvents,
      piwikEvents = _ref$piwikEvents === void 0 ? null : _ref$piwikEvents;
  addScript(appId);

  var share = function share() {
    if (!FB) {
      setTimeout(function () {
        return share();
      }, 1000); // retriggering share() after 1 second if FB does not exist

      return;
    }

    event(piwikEvents ? piwikEvents.start : ['share', 'facebook-start', page]);
    FB.ui({
      method: 'share',
      display: 'popup',
      href: url,
      quote: description
    }, FBcallback);
  };

  return share;
};

export default useFacebookShare;